import { z } from 'zod';

/**
 * F36 — attendance per (training session × player). Marked only by an assigned
 * club trainer (`hasClubTeamPermission`). Idempotent UPSERT on
 * (booking_id, player_user_id) — re-marking updates, never duplicates
 * (acceptance #7). Retroactive marking allowed within a lookback window (S7);
 * beyond it the API returns 422 ATTENDANCE_WINDOW_CLOSED.
 */

export const ATTENDANCE_STATUS_VALUES = ['present', 'absent', 'late', 'excused'] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUS_VALUES)[number];

/** Env-tunable via CLUB_ATTENDANCE_LOOKBACK_DAYS; this is the default ceiling. */
export const CLUB_ATTENDANCE_LOOKBACK_DAYS = 14;

export const ATTENDANCE_NOTE_MAX = 500;

export type TrainingAttendance = {
  readonly id: string;
  readonly bookingId: string;
  readonly playerUserId: string;
  readonly status: AttendanceStatus;
  readonly markedByUserId: string;
  readonly markedAt: string;
  readonly note: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Roster row + current mark for the attendance tracker UI. */
export type AttendanceRosterEntry = {
  readonly playerUserId: string;
  readonly playerDisplayName: string;
  readonly status: AttendanceStatus | null; // null = not yet marked
  readonly note: string | null;
  readonly markedAt: string | null;
};

/** A player's own attendance history row (own record only — S4). */
export type PlayerAttendanceEntry = {
  readonly bookingId: string;
  readonly teamId: string;
  readonly teamName: string;
  readonly startAt: string;
  readonly status: AttendanceStatus;
  readonly markedAt: string;
};

const AttendanceMarkSchema = z
  .object({
    playerUserId: z.string().uuid(),
    status: z.enum(ATTENDANCE_STATUS_VALUES),
    note: z.string().max(ATTENDANCE_NOTE_MAX).nullable().optional(),
  })
  .strict();

/** PUT /me/club/teams/:teamId/sessions/:bookingId/attendance — bulk upsert. */
export const MarkAttendanceInputSchema = z
  .object({ marks: z.array(AttendanceMarkSchema).min(1).max(60) })
  .strict();
export type MarkAttendanceInput = z.infer<typeof MarkAttendanceInputSchema>;
export type AttendanceMark = z.infer<typeof AttendanceMarkSchema>;
